import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import publicApi from '../helper/publicApi'
import { useLocation } from '../context/LocationContext'

const FooterOne = () => {
    // Get location context to determine which addresses to show
    const { isCasaRegion } = useLocation();
    
    // Get cached settings from localStorage immediately
    const getInitialSettings = () => {
        try {
            const cached = localStorage.getItem('siteSettings');
            if (cached) {
                return JSON.parse(cached);
            }
        } catch (e) {
            console.error('Error reading cache:', e);
        }
        return null;
    };

    const [settings, setSettings] = useState(getInitialSettings);
    const [loading, setLoading] = useState(true);

    // API base URL for images
    const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
    const STORAGE_BASE_URL = API_BASE_URL.replace('/api', '');

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const response = await publicApi.getSettings();
                if (response.success) {
                    setSettings(response.data);
                    // Update cache
                    localStorage.setItem('siteSettings', JSON.stringify(response.data));
                }
            } catch (error) {
                console.error('Error fetching footer settings:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchSettings();
    }, []);

    // Default values if settings not loaded - NO default for logo
    const footerLogo = settings?.footer_logo || null;
    // Default values if settings not loaded
    const defaultPhone1 = settings?.footer_phone1 || '06 61 70 00 52';
    const defaultPhone2 = settings?.footer_phone2 || '06 66 94 42 24';
    const defaultEmail = settings?.footer_email || 'contact@delicesbf.com';
    
    // Region specific contact info logic
    const getContactInfo = () => {
        if (isCasaRegion) {
            return {
                phone: settings?.footer_phone_casa || defaultPhone1,
                email: settings?.footer_email_casa || defaultEmail
            };
        } else {
            return {
                phone: settings?.footer_phone_fes || defaultPhone1,
                email: settings?.footer_email_fes || defaultEmail
            };
        }
    };

    const { phone: displayPhone, email: displayEmail } = getContactInfo();
    const phone2 = settings?.footer_phone2 || null;

    const facebook = settings?.footer_facebook || 'https://www.facebook.com/delicesbenkirane/';
    const instagram = settings?.footer_instagram || 'https://www.instagram.com/delices_benkirane/';
    const tiktok = settings?.footer_tiktok || 'https://www.tiktok.com/@delicesbenkirane';
    const youtube = settings?.footer_youtube || 'https://www.youtube.com/@delicesbenkirane';
    const whatsapp = settings?.footer_whatsapp || '';
    const copyright = settings?.footer_copyright || '© 2024 Délices du Fès. Tous droits réservés.';
    
    // Branch addresses from settings/API
    const branchFes1Address = settings?.branch_fes1_address || '36 Avenue Ibn Khatib, Fès';
    const branchFes2Address = settings?.branch_fes2_address || '';
    const branchCasaAddress = settings?.branch_casa_address || '';

    // Always show all branch addresses (Fes + Casa), regardless of visitor location
    const addressesToShow = [branchFes1Address, branchFes2Address, branchCasaAddress].filter(
        (addr) => addr && String(addr).trim()
    );
    
    // Menu links - parse if they're arrays, then filter out removed items
    const excludedInfoTitles = ['affiliation', 'nos fournisseurs', 'plan', 'communauté', 'communaute'];
    const excludedAccountTitles = ['comparer', 'ticket', 'souhaits'];

    const normalizeTitle = (title) =>
        String(title || '')
            .toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .trim();

    const infoLinks = (Array.isArray(settings?.footer_info_links) ? settings.footer_info_links : [])
        .filter((link) => !excludedInfoTitles.includes(normalizeTitle(link.title)));
    const supportLinks = Array.isArray(settings?.footer_support_links) ? settings.footer_support_links : [];
    const accountLinks = (Array.isArray(settings?.footer_account_links) ? settings.footer_account_links : [])
        .filter((link) => !excludedAccountTitles.includes(normalizeTitle(link.title)));

    // Get logo URL - handle /storage/ paths correctly
    const getLogoUrl = (logo) => {
        if (!logo) return null;
        if (logo.startsWith('http')) return logo;
        if (logo.startsWith('assets/')) return logo;
        if (logo.startsWith('/storage/')) return `${STORAGE_BASE_URL}${logo}`;
        return publicApi.getImageUrl(logo);
    };

    // Flag to temporarily hide the Support Client section
    const showSupportClient = false;

    const defaultInfoLinks = [
        { title: 'Devenir Vendeur', url: '/shop' },
        { title: 'Confidentialité', url: '/shop' },
    ];

    const defaultAccountLinks = [
        { title: 'Mon Compte', url: '/account' },
        { title: 'Commandes', url: '/my-orders' },
        { title: 'Panier', url: '/cart' },
    ];

    const visibleInfoLinks = infoLinks.length > 0 ? infoLinks : defaultInfoLinks;
    const visibleAccountLinks = accountLinks.length > 0 ? accountLinks : defaultAccountLinks;

    return (
        <footer className="footer py-80">
            <div className="container container-lg">
                <div className="footer-item-wrapper footer-layout">
                    <div className="footer-item footer-item--brand">
                        <div className="footer-item__logo">
                            <Link to="/">
                                {getLogoUrl(footerLogo) && (
                                    <img src={getLogoUrl(footerLogo)} alt="Logo" />
                                )}
                            </Link>
                        </div>
                        
                        {/* Show all branch addresses */}
                        {addressesToShow.map((address, index) => (
                            <div key={index} className="flex-align gap-12 mb-12">
                                <span className="footer-info-icon w-32 h-32 flex-center rounded-circle text-md flex-shrink-0">
                                    <i className="ph-fill ph-map-pin" />
                                </span>
                                <span className="text-sm text-gray-900">
                                    {address}
                                </span>
                            </div>
                        ))}
                        
                        {/* Phone numbers - dynamic based on region */}
                        <div className="flex-align gap-12 mb-12">
                            <span className="footer-info-icon w-32 h-32 flex-center rounded-circle text-md flex-shrink-0">
                                <i className="ph-fill ph-phone-call" />
                            </span>
                            <div className="flex-align gap-12 flex-wrap">
                                <a
                                    href={`tel:${displayPhone.replace(/\s/g, '')}`}
                                    className="footer-link text-sm text-gray-900 fw-bold"
                                >
                                    {displayPhone}
                                </a>
                                {isCasaRegion === false && phone2 && (
                                    <>
                                        <span className="text-sm text-main-600">|</span>
                                        <a
                                            href={`tel:${phone2.replace(/\s/g, '')}`}
                                            className="footer-link text-sm text-gray-900"
                                        >
                                            {phone2}
                                        </a>
                                    </>
                                )}
                            </div>
                        </div>
                        
                        {/* Email - dynamic based on region */}
                        <div className="flex-align gap-12 mb-0">
                            <span className="footer-info-icon w-32 h-32 flex-center rounded-circle text-md flex-shrink-0">
                                <i className="ph-fill ph-envelope" />
                            </span>
                            <a
                                href={`mailto:${displayEmail}`}
                                className="footer-link text-sm text-gray-900 fw-bold"
                            >
                                {displayEmail}
                            </a>
                        </div>
                    </div>

                    <div className="footer-item footer-item--links">
                        <h6 className="footer-item__title">Information</h6>
                        <ul className="footer-menu">
                            {visibleInfoLinks.map((link, index) => (
                                <li key={index} className={index === visibleInfoLinks.length - 1 ? 'mb-0' : 'mb-10'}>
                                    <Link to={link.url || '/shop'} className="footer-link text-gray-600">
                                        {link.title}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {showSupportClient && (
                        <div className="footer-item">
                            <h6 className="footer-item__title">Support Client</h6>
                            <ul className="footer-menu">
                                {supportLinks.length > 0 ? (
                                    supportLinks.map((link, index) => (
                                        <li key={index} className={index === supportLinks.length - 1 ? 'mb-0' : 'mb-10'}>
                                            <Link to={link.url || '/shop'} className="footer-link text-gray-600">
                                                {link.title}
                                            </Link>
                                        </li>
                                    ))
                                ) : null}
                            </ul>
                        </div>
                    )}

                    <div className="footer-item footer-item--links">
                        <h6 className="footer-item__title">Mon Compte</h6>
                        <ul className="footer-menu">
                            {visibleAccountLinks.map((link, index) => (
                                <li key={index} className={index === visibleAccountLinks.length - 1 ? 'mb-0' : 'mb-10'}>
                                    <Link to={link.url || '/shop'} className="footer-link text-gray-600">
                                        {link.title}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="footer-item footer-item--cta">
                        <h6 className="footer-item__title">Acheter en Déplacement</h6>
                        <p className="text-gray-600 mb-16 footer-cta-text">
                            Commandez nos délices artisanaux en ligne, où que vous soyez.
                            Qualité traditionnelle de Fès, livrée jusqu’à chez vous.
                        </p>
                        <ul className="flex-align gap-12">
                            {facebook && (
                                <li>
                                    <a
                                        href={facebook}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="footer-social-btn w-44 h-44 flex-center text-xl rounded-circle"
                                        aria-label="Facebook"
                                    >
                                        <i className="ph-fill ph-facebook-logo" />
                                    </a>
                                </li>
                            )}
                            {tiktok && (
                                <li>
                                    <a
                                        href={tiktok}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="footer-social-btn w-44 h-44 flex-center text-xl rounded-circle"
                                        aria-label="TikTok"
                                    >
                                        <i className="ph-fill ph-tiktok-logo" />
                                    </a>
                                </li>
                            )}
                            {instagram && (
                                <li>
                                    <a
                                        href={instagram}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="footer-social-btn w-44 h-44 flex-center text-xl rounded-circle"
                                        aria-label="Instagram"
                                    >
                                        <i className="ph-fill ph-instagram-logo" />
                                    </a>
                                </li>
                            )}
                            {youtube && (
                                <li>
                                    <a
                                        href={youtube}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="footer-social-btn w-44 h-44 flex-center text-xl rounded-circle"
                                        aria-label="YouTube"
                                    >
                                        <i className="ph-fill ph-youtube-logo" />
                                    </a>
                                </li>
                            )}
                            {whatsapp && (
                                <li>
                                    <a
                                        href={`https://wa.me/${whatsapp.replace(/\s/g, '')}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="footer-social-btn w-44 h-44 flex-center text-xl rounded-circle"
                                        aria-label="WhatsApp"
                                    >
                                        <i className="ph-fill ph-whatsapp-logo" />
                                    </a>
                                </li>
                            )}
                        </ul>
                    </div>
                </div>
            </div>
        </footer>
    )
}

export default FooterOne